<template>
    <v-dialog v-model="dialogModel" max-width="900">

        <toolbar-page title="Preview question" icon="mdi-eye">
            <template slot="buttons">

                <v-tooltip top color="primary">
                    <template v-slot:activator="{ on }">
                        <v-btn @click="dialogModel=false" v-on="on" icon>
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                    <span>Close</span>
                </v-tooltip>
            </template>
        </toolbar-page>

        <v-card tile color="grey darken-3">
            <v-container fluid class="pa-0 pl-2 pr-2">
                <v-row dense>
                    <v-col cols="6">
                        <v-select v-model="selectedSubPanel"
                                  :items="subPanels"
                                  item-text="name"
                                  item-value="id"
                                  return-object
                                  @change="subPanelSelected"
                                  solo
                                  hide-details
                        ></v-select>
                    </v-col>

                    <v-col cols=6>

                        <v-select v-model="selectedSubPanelLocale"
                                  :items="availableLocales"
                                  item-text="code"
                                  item-value="id"
                                  return-object
                                  solo
                                  hide-details
                        ></v-select>
                    </v-col>
                </v-row>
            </v-container>
        </v-card>

        <v-card light tile :color="backgroundColor" :loading="loading">

            <v-card-text class="pt-5">
                <text-question
                    v-if="variable.type === 'text' && selectedSubPanel"
                    v-model="model"
                    :variable="variable"
                    :required="isAnswerRequired(variable)"
                    :locale="selectedSubPanelLocale"
                    :background-color="backgroundColor"
                    :button-color="actionButtonColor"
                    :translations="subPanelTranslations"
                ></text-question>

                <keyword-question
                    v-if="variable.type === 'keyword' && selectedSubPanel"
                    v-model="model"
                    :variable="variable"
                    :required="isAnswerRequired(variable)"
                    :background-color="backgroundColor"
                    :button-color="actionButtonColor"
                    :locale="selectedSubPanelLocale"
                    :translations="subPanelTranslations"
                ></keyword-question>

                <number-question
                    v-if="variable.type === 'number' && selectedSubPanel"
                    v-model="model"
                    :variable="variable"
                    :required="isAnswerRequired(variable)"
                    :locale="selectedSubPanelLocale"
                    :background-color="backgroundColor"
                    :button-color="actionButtonColor"
                    :translations="subPanelTranslations"
                ></number-question>

                <checkbox-question
                    v-if="variable.type === 'checkbox' && selectedSubPanel"
                    v-model="model"
                    :variable="variable"
                    :required="isAnswerRequired(variable)"
                    :locale="selectedSubPanelLocale"
                    :background-color="backgroundColor"
                    :button-color="actionButtonColor"
                    :translations="subPanelTranslations"
                ></checkbox-question>

                <radio-question
                    v-if="variable.type === 'radio' && selectedSubPanel"
                    v-model="model"
                    :variable="variable"
                    :required="isAnswerRequired(variable)"
                    :locale="selectedSubPanelLocale"
                    :background-color="backgroundColor"
                    :button-color="actionButtonColor"
                    :translations="subPanelTranslations"
                ></radio-question>

                <email-question
                    v-if="variable.type === 'email'  && selectedSubPanel"
                    v-model="model"
                    :variable="variable"
                    :required="isAnswerRequired(variable)"
                    :locale="selectedSubPanelLocale"
                    :background-color="backgroundColor"
                    :button-color="actionButtonColor"
                    :translations="subPanelTranslations"
                ></email-question>

                <date-question
                    v-if="variable.type === 'date' && selectedSubPanel"
                    v-model="model"
                    :variable="variable"
                    :locale="selectedSubPanelLocale"
                    :required="isAnswerRequired(variable)"
                    :background-color="backgroundColor"
                    :component-data="{minAge: selectedSubPanel.minimum_age_to_join}"
                    :button-color="actionButtonColor"
                    :translations="subPanelTranslations"
                ></date-question>

                <phone-question
                    v-if="variable.type === 'phone' && selectedSubPanel"
                    v-model="model"
                    :variable="variable"
                    :locale="selectedSubPanelLocale"
                    :required="isAnswerRequired(variable)"
                    :background-color="backgroundColor"
                    :button-color="actionButtonColor"
                    :translations="subPanelTranslations"
                ></phone-question>

            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>

import TextQuestion from "../../../components/UI/DynamicProflingQuestions/TextQuestion";
import KeywordQuestion from "../../../components/UI/DynamicProflingQuestions/KeywordQuestion";
import CheckboxQuestion from "../../../components/UI/DynamicProflingQuestions/CheckboxQuestion";
import DateQuestion from "../../../components/UI/DynamicProflingQuestions/DateQuestion";
import NumberQuestion from "../../../components/UI/DynamicProflingQuestions/NumberQuestion";
import RadioQuestion from "../../../components/UI/DynamicProflingQuestions/RadioQuestion";
import EmailQuestion from "../../../components/UI/DynamicProflingQuestions/EmailQuestion";
import ToolbarPage from "../../../components/UI/ToolbarPage";
import {mapState, mapActions} from 'vuex';
import {registrationQuestionsMixin} from "../../../components/mixins/registrationQuestionsMixin";
import PhoneQuestion from "../../../components/UI/DynamicProflingQuestions/PhoneQuestion";

export default {

    mixins: [registrationQuestionsMixin],

    name: "ViewQuestionDialog",

    components: {
        PhoneQuestion,
        ToolbarPage,
        TextQuestion,
        CheckboxQuestion,
        DateQuestion,
        NumberQuestion,
        RadioQuestion,
        EmailQuestion,
        KeywordQuestion
    },

    props: {
        variable: {
            type: Object,
            required: true,
        },

        value: {
            type: Boolean,
            required: true,
        },

        answer: {
            type: String | Number | Array | null,
            required: false,
        },
    },

    computed: {
        dialogModel: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },

        ...mapState('SubPanels', [
            'subPanels',
            'subPanelTranslations'
        ]),

        availableLocales() {
            if (this.selectedSubPanel) {

                return this.$store.state.PanelSettings.settings.locales.filter(locale => {
                    const foundInSubPanel = this.selectedSubPanel.locales.find(l => l.id === locale.id);
                    const foundInQuestion = this.variable.locales.find(l => l.locale_id === locale.id);
                    return foundInSubPanel !== null && foundInQuestion != null;
                })
            }
        },

        subPanelLocales() {
            if (this.selectedSubPanel) {
                return this.selectedSubPanel.locales;
            }
        },

        backgroundColor() {
            if (this.selectedSubPanel) {
                return this.selectedSubPanel.landing_page_settings.surveyBoxColor;
            }
        },

        actionButtonColor() {
            return this.selectedSubPanel.landing_page_settings.actionButtonColor;
        }
    },

    watch: {

        answer(answer) {
            this.model = answer;
        },

        dialogModel(dialogVisible) {

            if (dialogVisible) {

                this.loadSubPanels()
                    .then(() => {

                        this.selectedSubPanel = this.subPanels[0];

                        this.autoSelectLocale();
                        this.loadTranslations();
                    })
                    .catch(error => {

                        console.log(error);

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'OOPS. We failed to load Sub Panels!'
                        })
                    })

            }
        },
    },

    data() {
        return {
            model: null,
            selectedSubPanel: null,
            selectedSubPanelLocale: null,
            loading: false,
        }
    },

    methods: {
        ...mapActions('SubPanels', [
            'loadSubPanels',
            'loadSubPanelTranslations'
        ]),

        subPanelSelected() {
            this.autoSelectLocale();
        },

        autoSelectLocale() {
            this.selectedSubPanelLocale = this.selectedSubPanel.locales[0];
            this.loadTranslations();
        },

        loadTranslations() {

            this.loading = true;

            this.loadSubPanelTranslations(this.selectedSubPanel.id)

                .catch(error => {

                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'OOPS. We failed to load Sub Panel translations!'
                    })
                })
                .finally(() => {
                    this.loading = false;
                })
        },

        isAnswerRequired(variable) {
            if (variable.dynamic_profiling_validators) {
                return !!variable.dynamic_profiling_validators.find(validator => validator.type === 'required');
            }
            return this.mandatoryQuestions.includes(variable.name);
        }
    },

    mounted() {
        this.model = this.answer;
    },
}
</script>
